// src/utils/authentication/errorMessages.js
/**
 * Translate Firebase Auth error codes to human-friendly UI strings.
 *
 * Firebase returns slightly different codes depending on context:
 *   • **JavaScript SDK** – e.g. `auth/email-already-in-use`
 *   • **REST API**      – e.g. `EMAIL_EXISTS`
 *   • **Admin SDK**     – constants in `AuthErrorCodes`
 *
 * This helper normalises those variants so components can show a short,
 * descriptive message without duplicating switch/if logic.
 *
 * @param {string | keyof import("firebase/auth").AuthErrorCodes} code
 *        Raw error code from `catch (err)`.
 * @param {string} [fallback="Something went wrong. Please try again."]
 *        Message to return when the code isn’t recognised.
 *
 * @returns {string} A message safe to display to end-users.
 */

import { AuthErrorCodes } from "firebase/auth";

export function friendlyMessage(
  code,
  fallback = "Something went wrong. Please try again."
) {
  switch (code) {
    /* Duplicate e-mail ------------------------------------------------ */
    case AuthErrorCodes.EMAIL_EXISTS: // Admin SDK
    case "auth/email-already-in-use": // JS SDK
    case "EMAIL_EXISTS": // REST API
      return "That email is already registered.";

    /* Invalid e-mail format ------------------------------------------ */
    case AuthErrorCodes.INVALID_EMAIL:
    case "INVALID_EMAIL":
      return "Please enter a valid email address.";

    /* Weak password --------------------------------------------------- */
    case AuthErrorCodes.WEAK_PASSWORD:
    case "auth/weak-password":
    case "WEAK_PASSWORD":
      return "Your password is too weak.";

    /* Sign-in provider disabled or throttled ------------------------- */
    case AuthErrorCodes.OPERATION_NOT_ALLOWED:
    case "OPERATION_NOT_ALLOWED":
      return "Email/password sign-in is currently disabled.";
    case "TOO_MANY_ATTEMPTS_TRY_LATER":
      return "Too many attempts. Please wait a bit and try again.";

    case AuthErrorCodes.INVALID_PASSWORD:
    case AuthErrorCodes.USER_DELETED:
    case "auth/invalid-credential":
      return "Incorrect email or password.";
    /* Fallback -------------------------------------------------------- */
    default:
      return fallback;
  }
}
